/**
 * Module: web/src/lib/errorBeacon.ts
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.0
 * Date: 2026-07-14
 *
 * Client-side error capture + staging beacon (contract
 * error-observability-api v1.0.0 SS4; finding #2326 + scratch #913 spec;
 * durable #19). The half of observability NO server-side log can ever hold:
 * code errors thrown before a request exists (#2327), requests killed inside
 * the browser (content blockers), and requests lost to connection/edge
 * failures.
 *
 * Design (Robert's spec):
 *   - STAGING QUEUE in localStorage ('ops_error_queue_v1', cap 50 FIFO):
 *     errors survive tab close, browser restart, and connection loss.
 *   - Each entry minted at capture with a client_error_id UUID + occurred_at,
 *     so the RETRY EVERY 30 SECONDS until delivered can never double-insert
 *     (the server's ON CONFLICT DO NOTHING pairs with it).
 *   - Delivery: POST /api/client-log batches (<=20, oldest first),
 *     credentials included so the session attributes rows server-side;
 *     flushed on boot / online / visibility-visible / a 2 s post-capture
 *     debounce / the 30 s interval; best-effort sendBeacon on pagehide.
 *   - SELF-SAFETY: raw fetch (never useApi -- no recursion), every path
 *     try/catch-swallowed, its own delivery failures are NEVER re-staged,
 *     10 s burst dedupe (same kind+message+page collapses into detail.count).
 *
 * Import discipline: this module imports NOTHING app-side (useApi imports IT;
 * a cycle here would be load-bearing). The SPA route path comes from a
 * provider registered by main.ts; location.pathname is the fallback.
 */

export type BeaconKind =
  | 'code_error'
  | 'unhandled_rejection'
  | 'api_error'
  | 'request_failed';

export interface BeaconDetail {
  stack?: string;
  status?: number;
  method?: string;
  path?: string;
  code?: string | null;
  app_shell?: string;
  count?: number;
  last_seen_at?: string;
  [k: string]: unknown;
}

interface QueuedError {
  client_error_id: string;
  occurred_at: string; // ISO, client clock (true capture moment)
  kind: BeaconKind;
  message: string;
  page?: string;
  interaction?: string;
  detail?: BeaconDetail;
}

const STORAGE_KEY = 'ops_error_queue_v1';
const QUEUE_MAX = 50; // FIFO drop-oldest (contract SS4.1)
const BATCH_MAX = 20; // server's per-request item cap (contract SS5.1)
const RETRY_MS = 30_000; // Robert's spec: retry every 30 s until delivered
const CAPTURE_FLUSH_DEBOUNCE_MS = 2_000;
const DEDUPE_WINDOW_MS = 10_000; // burst collapse window (contract SS4.3)
const MESSAGE_MAX = 2_000;
const STACK_MAX = 2_000;

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

// In-memory queue mirror; localStorage is the durable copy (best-effort --
// private-mode/quota failures degrade to memory-only for this tab's life).
let queue: QueuedError[] = [];
let storageOk = true;
let installed = false;
let delivering = false;
let retryTimer: number | null = null;
let debounceTimer: number | null = null;
let appShell: string | undefined;
let pageProvider: () => string = () =>
  typeof location !== 'undefined' ? location.pathname : '';

// ---------------------------------------------------------------------------
// internals
// ---------------------------------------------------------------------------

function uuid(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  // RFC-4122-ish fallback (old WebViews): timestamp + random.
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`;
}

function loadQueue(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      queue = parsed
        .filter((e): e is QueuedError =>
          !!e && typeof e === 'object'
          && typeof (e as QueuedError).client_error_id === 'string'
          && typeof (e as QueuedError).message === 'string')
        .slice(-QUEUE_MAX);
    }
  } catch {
    storageOk = false;
  }
}

function persist(): void {
  if (!storageOk) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    storageOk = false; // quota/private mode: memory-only from here on
  }
}

/** True when this error is about the beacon's own delivery path -- NEVER
 *  re-staged (contract SS4.5: the beacon must not recurse on its failures). */
function isSelfReferential(message: string, detail?: BeaconDetail): boolean {
  if (detail?.path && String(detail.path).includes('/client-log')) return true;
  return message.includes('/client-log');
}

function enqueue(entry: QueuedError): void {
  // Burst dedupe (SS4.3): identical kind+message+page within 10 s collapses
  // into the queued entry's count instead of a new slot.
  const now = Date.now();
  for (let i = queue.length - 1; i >= 0; i--) {
    const prior = queue[i];
    if (prior.kind === entry.kind && prior.message === entry.message
        && prior.page === entry.page) {
      const priorAt = Date.parse(prior.occurred_at);
      if (Number.isFinite(priorAt) && now - priorAt < DEDUPE_WINDOW_MS) {
        prior.detail = prior.detail ?? {};
        prior.detail.count = (prior.detail.count ?? 1) + 1;
        prior.detail.last_seen_at = entry.occurred_at;
        persist();
        return;
      }
      break; // only the newest sibling is a collapse candidate
    }
  }
  queue.push(entry);
  if (queue.length > QUEUE_MAX) queue = queue.slice(-QUEUE_MAX); // drop oldest
  persist();
  scheduleSoonFlush();
}

function scheduleSoonFlush(): void {
  if (debounceTimer !== null) return;
  debounceTimer = window.setTimeout(() => {
    debounceTimer = null;
    void deliver();
  }, CAPTURE_FLUSH_DEBOUNCE_MS);
}

interface SinkRuling {
  accepted?: string[];
  duplicate?: string[];
  rejected?: { client_error_id: string | null }[];
}

/** Remove every id the server RULED on (accepted/duplicate/rejected) -- a
 *  permanently-invalid item must not wedge the retry loop (contract SS4.4). */
function removeRuled(ruling: SinkRuling): void {
  const ruled = new Set<string>([
    ...(ruling.accepted ?? []),
    ...(ruling.duplicate ?? []),
    ...(ruling.rejected ?? []).map((r) => r.client_error_id ?? ''),
  ]);
  queue = queue.filter((e) => !ruled.has(e.client_error_id));
  persist();
}

async function deliver(): Promise<void> {
  if (delivering || queue.length === 0) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  delivering = true;
  try {
    const batch = queue.slice(0, BATCH_MAX);
    // Raw fetch, NEVER useApi: the beacon's own failures must not loop back
    // through the capture sites (SS4.5).
    const res = await fetch(`${API_BASE}/client-log`, {
      method: 'POST',
      credentials: 'include', // session cookie -> server-side attribution
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ errors: batch }),
    });
    if (res.ok) {
      const ruling = (await res.json().catch(() => null)) as SinkRuling | null;
      if (ruling) removeRuled(ruling);
      if (queue.length > 0) scheduleSoonFlush(); // drain remainder promptly
    } else if (res.status === 400 || res.status === 413) {
      // Whole-batch shape reject: unprocessable forever -- drop the batch so
      // the queue converges (our own client never builds these; this guards
      // corrupted storage).
      const ids = new Set(batch.map((e) => e.client_error_id));
      queue = queue.filter((e) => !ids.has(e.client_error_id));
      persist();
    }
    // 429/5xx: leave queued; the 30 s tick retries.
  } catch {
    // Network failure delivering telemetry: swallow, keep queued, retry later.
  } finally {
    delivering = false;
  }
}

function pagehideFlush(): void {
  try {
    if (queue.length === 0 || typeof navigator === 'undefined'
        || !navigator.sendBeacon) return;
    // Fire-and-forget; entries STAY queued -- if the beacon landed, the
    // idempotency key makes the next tick's re-send a server-side no-op.
    const batch = queue.slice(0, BATCH_MAX);
    navigator.sendBeacon(
      `${API_BASE}/client-log`,
      new Blob([JSON.stringify({ errors: batch })], { type: 'text/plain' }),
    );
  } catch { /* never throw on the way out */ }
}

function detectAppShell(): string | undefined {
  try {
    const el = document.querySelector('script[type="module"][src*="/assets/index-"]');
    const src = el?.getAttribute('src');
    return src ? src.split('/').pop() : undefined;
  } catch {
    return undefined;
  }
}

function baseDetail(extra?: BeaconDetail): BeaconDetail | undefined {
  const d: BeaconDetail = { ...(extra ?? {}) };
  if (typeof d.stack === 'string') d.stack = d.stack.slice(0, STACK_MAX);
  if (appShell) d.app_shell = appShell;
  return Object.keys(d).length > 0 ? d : undefined;
}

function report(kind: BeaconKind, message: unknown, interaction?: string,
                detail?: BeaconDetail): void {
  try {
    const msg = String(message ?? 'unknown error').slice(0, MESSAGE_MAX);
    if (!msg || isSelfReferential(msg, detail)) return;
    enqueue({
      client_error_id: uuid(),
      occurred_at: new Date().toISOString(),
      kind,
      message: msg,
      page: pageProvider() || undefined,
      interaction: interaction ? interaction.slice(0, 200) : undefined,
      detail: baseDetail(detail),
    });
  } catch { /* capture must never break the app it observes */ }
}

// ---------------------------------------------------------------------------
// public surface
// ---------------------------------------------------------------------------

/**
 * The #2327 class: a CAUGHT code bug that a catch block would otherwise only
 * toast. Call from catch paths with a human interaction label ("mark-paid
 * confirm") so the Errors view shows what the user was doing.
 */
export function reportCaughtError(err: unknown, interaction: string): void {
  const message = err instanceof Error
    ? `${err.name}: ${err.message}` : String(err);
  const stack = err instanceof Error && err.stack ? err.stack : undefined;
  report('code_error', message, interaction, stack ? { stack } : undefined);
}

/**
 * useApi's hook (called just before an ApiError is thrown). status 0 =
 * request_failed (never reached the portal -- the class with zero server
 * trace); status > 0 = api_error (the server answered an error).
 */
export function reportApiFailure(opts: {
  status: number; method: string; path: string;
  code: string | null; message: string;
}): void {
  report(
    opts.status === 0 ? 'request_failed' : 'api_error',
    opts.message,
    undefined,
    { status: opts.status, method: opts.method, path: opts.path,
      code: opts.code },
  );
}

/**
 * Boot wiring (main.ts): load the persisted queue, hook the global capture
 * sites, start the 30 s retry + flush triggers, deliver anything staged by a
 * previous session (the crashed-tab / offline-shift recovery path).
 */
export function installErrorBeacon(opts?: { page?: () => string }): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  try {
    if (opts?.page) pageProvider = opts.page;
    appShell = detectAppShell();
    loadQueue();

    window.addEventListener('error', (event: ErrorEvent) => {
      // Resource-load errors fire on window without ErrorEvent fields --
      // only real script errors carry message/error here.
      if (!event.message) return;
      const stack = event.error instanceof Error ? event.error.stack : undefined;
      report('code_error', event.message, undefined,
             stack ? { stack } : undefined);
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const reason: unknown = event.reason;
      // ApiError rejections were already reported by useApi's hook -- name
      // check (not instanceof) keeps this module import-free (no cycle).
      if (reason instanceof Error && reason.name === 'ApiError') return;
      const message = reason instanceof Error
        ? `${reason.name}: ${reason.message}` : String(reason);
      const stack = reason instanceof Error && reason.stack
        ? reason.stack : undefined;
      report('unhandled_rejection', message, undefined,
             stack ? { stack } : undefined);
    });

    window.addEventListener('online', () => void deliver());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void deliver();
    });
    window.addEventListener('pagehide', pagehideFlush);

    retryTimer = window.setInterval(() => void deliver(), RETRY_MS);
    void deliver(); // boot flush: a prior session's staged errors go now
  } catch { /* installation failure degrades to no telemetry, never a crash */ }
}

/** Test/diagnostic surface (E2E asserts staging + delivery). */
export function beaconQueueSnapshot(): { length: number; ids: string[] } {
  return { length: queue.length, ids: queue.map((e) => e.client_error_id) };
}

export function stopErrorBeacon(): void {
  if (retryTimer !== null) window.clearInterval(retryTimer);
  retryTimer = null;
  installed = false;
}
